import { Registry, type Identified } from './Registry';

/**
 * Zorluk seviyeleri.
 * Zorluk yalnızca düşman canını artırmaz: kaynak bolluğu, açlık hızı,
 * dayanıklılık aşınması, AI agresifliği, ölüm cezası ve ekonomi de değişir.
 * Tüm sistemler bu çarpanları merkezden okur.
 */

export type DeathPenalty = 'keep' | 'dropHotbar' | 'dropAll';

export interface DifficultyDef extends Identified {
  name: string;
  desc: string;
  /** Kaynak düşme miktarı çarpanı. */
  resourceYield: number;
  /** Damar sıklığı çarpanı. */
  oreFrequency: number;
  /** Düşman hasarı / canı. */
  enemyDamage: number;
  enemyHealth: number;
  /** Düşmanın oyuncuyu fark etme mesafesi çarpanı. */
  aggroRange: number;
  /** Gece düşman doğma çarpanı. */
  nightSpawn: number;
  /** Açlık tükenme hızı. */
  hungerRate: number;
  /** Stamina tükenme hızı. */
  staminaDrain: number;
  /** Alet/zırh aşınma hızı. */
  durabilityWear: number;
  /** Ölünce ne olur. */
  deathPenalty: DeathPenalty;
  /** Sandık/loot cömertliği. */
  lootBonus: number;
  /** Zindan düşman yoğunluğu. */
  dungeonScale: number;
  /** BEDO satın alma fiyatı çarpanı (satış fiyatına ters etki eder). */
  shopPrice: number;
  /** Can yenilenme hızı çarpanı (0 = kapalı). */
  regen: number;
}

export const difficulties = new Registry<DifficultyDef>('difficulty');

difficulties.register(
  {
    id: 'easy',
    name: 'Kolay',
    desc: 'Bol kaynak, yumuşak düşmanlar, rahat açlık. Keşif odaklı oyun.',
    resourceYield: 1.75,
    oreFrequency: 1.6,
    enemyDamage: 0.5,
    enemyHealth: 0.7,
    aggroRange: 0.65,
    nightSpawn: 0.5,
    hungerRate: 0.55,
    staminaDrain: 0.6,
    durabilityWear: 0.5,
    deathPenalty: 'keep',
    lootBonus: 1.4,
    dungeonScale: 0.7,
    shopPrice: 0.8,
    regen: 1.6,
  },
  {
    id: 'normal',
    name: 'Normal',
    desc: 'Oyunun tasarlandığı denge.',
    resourceYield: 1,
    oreFrequency: 1,
    enemyDamage: 1,
    enemyHealth: 1,
    aggroRange: 1,
    nightSpawn: 1,
    hungerRate: 1,
    staminaDrain: 1,
    durabilityWear: 1,
    deathPenalty: 'dropHotbar',
    lootBonus: 1,
    dungeonScale: 1,
    shopPrice: 1,
    regen: 1,
  },
  {
    id: 'hard',
    name: 'Zor',
    desc: 'Kaynak kıt, düşmanlar acımasız, açlık ve dayanıklılık hayati. Ölünce her şeyi düşürürsün.',
    resourceYield: 0.6,
    oreFrequency: 0.55,
    enemyDamage: 1.9,
    enemyHealth: 1.6,
    aggroRange: 1.5,
    nightSpawn: 1.8,
    hungerRate: 1.6,
    staminaDrain: 1.45,
    durabilityWear: 1.8,
    deathPenalty: 'dropAll',
    lootBonus: 0.8,
    dungeonScale: 1.5,
    shopPrice: 1.35,
    regen: 0.4,
  },
);

export const DEFAULT_DIFFICULTY = 'normal';

export function difficultyOf(id: string | null | undefined): DifficultyDef {
  return difficulties.get(id) ?? difficulties.require(DEFAULT_DIFFICULTY);
}
